/* eslint-disable no-empty-function */
/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2022
 * @format
 * @flow
 */

import * as signalR from '@microsoft/signalr';
import pako from 'pako';

/**
 * The CApiWebSocketClient.js is WebSocket JavaScript client for subscribing to real-time change notification messages for
 * store checks and notifications by connecting to the CApi (Caching API) via WebSockets.
 */
class CApiWebSocketClient {
  // eslint-disable-next-line no-useless-constructor

  connection = null;

  subscribedToStoreChecks = false;

  subscribedToStoreCheckPreps = false

  subscribedToStoreNotifications = false;

  /**
   * Executed when a successful connection has been made
   * @example
   * client.onConnected = () => { console.log('Connected'); }
   */
  onConnected = null;

  /**
   * Executed when a message has been received
   * @param message
   * @example
   * client.onMessageReceived = (message) => { console.log('Message receive', message); }
   */
  onMessageReceived = null;

  /**
   * Executed when the client was disconnected and is attempting to reconnect
   * @param error
   * @example
   * client.onReconnecting = (error) => { console.log('Reconnecting...', error); }
   */
  onReconnecting = null;

  /**
   * Executed when the client has succussfully reconnected
   * @example
   * client.onReconnected = () => { console.log('Reconnected'); }
   */
  onReconnected = null;

  /**
   * Executed when the client connection has been permanently disconnected.
   * The client automatically attempts to reconnect indefinitely
   * so this function should never be called but is included for completeness
   * @example
   * client.onClose = (error) => { console.log('Client connection closed', error); }
   */
  onClose = null;

  // Gets a random integer with min and max included
  randomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1) + min);
  };

  // Check whether it is function or not
  isFunction = (obj) => {
    return typeof obj === 'function';
  };

  decompress = (compressedBase64String) => {
    // decode base64 (convert ascii to binary)
    const decodedData = atob(compressedBase64String);

    // convert binary string to character number array
    const compressedData = Array.from(decodedData).map((c) => c.charCodeAt(0));

    // turn number array into byte array
    const binaryData = new Uint8Array(compressedData);

    // decompress byte array using pako
    // pako - zlib port to JavaScript, very fast
    // results are binary equal to well known zlib
    const decompressedBinaryData = pako.inflate(binaryData);

    // convert decompressed byte array to unicode string
    const decompressedString = new TextDecoder('utf-8').decode(decompressedBinaryData);

    return decompressedString;
  };

  /**
   * Subscribes to store checks.
   * Messages will be sent to the onMessageReceived function.
   * @example
   * client.subscribeToStoreChecks();
   */
  subscribeToStoreChecks = () => {
    return this.connection.invoke('subscribeToStoreChecks')
      .then((data) => {
        this.subscribedToStoreChecks = true;
        return data;
      });
  };

  subscribeToStoreCheckPreps = () => {
    return this.connection.invoke('subscribeToStoreCheckPreps')
      .then((data) => {
        this.subscribedToStoreCheckPreps = true;
        return data;
      });
  };

  /**
   * Subscribes to store notifications.
   * Messages will be sent to the onMessageReceived function.
   * @example
   * client.subscribeToStoreNotifications();
   */
  subscribeToStoreNotifications = () => {
    return this.connection.invoke('subscribeToStoreNotifications')
      .then((data) => {
        this.subscribedToStoreNotifications = true;
        return data;
      });
  };

  /**
   * Connects to the WebSocket server
   * @param {string} baseUrl - The CApi base URL (from the 'cApiHostname' field of the 'get store info for device' response)
   * @param {string} apiKey - The device specific apiKey (from the 'apiKey' field of the 'device activation' response)
   * @param {string} deviceExternalId - The device specific externalId (from the 'deviceExternalId' field of the 'device activation' response)
   * @example
   * client.connect('https://eapi.dev.onedine.com', 'ab068ef85b0c4c339104183442cd0de6', 'a15555b1025e449492de34e9a44803a9');
   */
  connect = (baseUrl, apiKey, deviceExternalId) => {
    if (this.connection) {
      this.connection.stop();
    }

    const url = `${baseUrl}/ws/cache?apiKey=${apiKey}&deviceExternalId=${deviceExternalId}`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets
      })
      .configureLogging(signalR.LogLevel.Debug)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // retry forever with an exponential back-off to reduce load and prevent a self-inflicted flood attack
          // on the server when the server comes back online
          if (retryContext.previousRetryCount === 0) {
            // retry immediately on 1st attempt
            return 0;
          } if (retryContext.previousRetryCount === 1) {
            // retry after 2 seconds on 2nd attempt
            return 2000;
          } if (retryContext.elapsedMilliseconds < 60000) {
            // if we've been reconnecting for less than 60 seconds so far,
            // wait between 2 and 10 seconds before the next reconnect attempt.
            return this.randomInt(2000, 10000);
          }
          // if we have been reconnecting for more than 60 seconds so far,
          // wait between 30 and 60 seconds before the next reconnect attempt.
          return this.randomInt(30000, 60000);
        }
      })
      .build();

    this.connection.onreconnecting((error) => {
      if (this.isFunction(this.onReconnecting)) {
        this.onReconnecting(error);
      }
    });

    this.connection.onreconnected((connectionId) => {
      console.info('connectionId - ', connectionId);
      // on reconnection, if previously subscribed, then automatically re-subscribed again
      if (this.subscribedToStoreChecks) {
        this.subscribeToStoreChecks();
      }
      if(this.subscribedToStoreCheckPreps) {
        this.subscribeToStoreCheckPreps()
      }
      if (this.subscribedToStoreNotifications) {
        this.subscribeToStoreNotifications();
      }
      if (this.isFunction(this.onReconnected)) {
        this.onReconnected();
      }
    });

    this.connection.onclose((error) => {
      if (this.isFunction(this.onClose)) {
        this.onClose(error);
      }
    });

    this.connection.on('messageReceived', (compressedJson) => {
      try {
        if (this.isFunction(this.onMessageReceived)) {
          const decompressedJson = this.decompress(compressedJson);
          const message = JSON.parse(decompressedJson);
          this.onMessageReceived(message);
        }
      } catch (e) {
        console.error('messageReceived error', e, compressedJson);
      }
    });

    // retry initial start attempt forever waiting between 2 and 10 seconds before the next start attempt
    try {
      this.connection.start()
        .then(() => {
          if (this.isFunction(this.onConnected)) {
            this.onConnected();
          }
        })
        .catch((err) => {
          console.error(err.toString());
          setTimeout(() => {
            this.connect(baseUrl, apiKey, deviceExternalId);
          }, this.randomInt(2000, 10000));
        });
    } catch (e) {
      setTimeout(() => {
        this.connect(baseUrl, apiKey, deviceExternalId);
      }, this.randomInt(2000, 10000));
    }
  };
}

export default CApiWebSocketClient;

/**
 * References:-
 *
 * SignalR Javascript client docs:
 * Overview: https://docs.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-3.1
 * Client api: https://docs.microsoft.com/en-us/javascript/api/@microsoft/signalr/hubconnection?view=signalr-js-latest
 * Npm package: https://www.npmjs.com/package/@microsoft/signalr/v/6.0.1
 *
 * Pako: zlib port to javascript, very fast!
 * Overview: https://nodeca.github.io/pako/
 * Npm package: https://www.npmjs.com/package/pako/v/2.0.3
 * Github: https://github.com/nodeca/pako
 */
